import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { userManagementApi, publisherApi, producerService } from "@/api";
import { STORAGE_KEYS } from "@/api/config";
import type { UserProfile, UserRole, LegalEntityType } from "@/api/types/user-management.types";

// JWT payload structure
interface JwtPayload {
  sub?: string;
  email?: string;
  roles?: string[];
  exp?: number;
  iat?: number;
}

// Helper function to decode JWT and extract payload
const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Helper function to check if token is expired
 *
 * Edge cases handled:
 * - Token without exp claim: treated as expired (returns true)
 * - Malformed token that can't be decoded: treated as expired (returns true)
 * - 5-second buffer before actual expiration to prevent:
 *   - API calls starting just before expiry and failing mid-request
 *   - Clock skew between client and server
 *   - Race conditions between check interval and actual expiration
 */
const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  // 5-second buffer: redirect slightly before actual expiration
  return Date.now() >= payload.exp * 1000 - 5000;
};

/**
 * Session timeout check interval (30 seconds)
 *
 * Trade-offs:
 * - Shorter interval = faster detection but more CPU usage
 * - Longer interval = delayed detection but less overhead
 * - 30 seconds is a reasonable balance for most use cases
 */
const SESSION_CHECK_INTERVAL = 30000;

interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  username?: string;
  roles?: UserRole[];
  profileData?: UserProfile['profileData'];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    firstName: string,
    lastName: string,
    email: string,
    officialPhoneNumber: string,
    password: string,
    publisherNameEnglish: string,
    publisherNameArabic: string,
    legalEntityType: string,
    commercialRegistrationNumber: string,
    countryOfRegistration: string,
    businessLicense: File | null,
    legalRepresentativeName: string,
    nationalIdPassportNumber: string,
    profileType?: 'PUBLISHER' | 'PRODUCER'
  ) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  // Publisher ID caching
  publisherId: string | null;
  publisherIdLoading: boolean;
  publisherIdError: string | null;
  fetchPublisherId: () => Promise<string | null>;
  // Producer ID caching (mirrors publisherId pattern)
  producerId: string | null;
  producerIdLoading: boolean;
  producerIdError: string | null;
  fetchProducerId: () => Promise<string | null>;
  // Profile
  profile: UserProfile | null;
  profileLoading: boolean;
  profileError: string | null;
  fetchProfile: (force?: boolean) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Publisher ID caching state
  const [publisherId, setPublisherId] = useState<string | null>(null);
  const [publisherIdLoading, setPublisherIdLoading] = useState(false);
  const [publisherIdError, setPublisherIdError] = useState<string | null>(null);
  const [publisherIdFetched, setPublisherIdFetched] = useState(false); // Track if we already attempted fetch

  // Producer ID caching state (mirrors publisherId pattern)
  const [producerId, setProducerId] = useState<string | null>(null);
  const [producerIdLoading, setProducerIdLoading] = useState(false);
  const [producerIdError, setProducerIdError] = useState<string | null>(null);
  const [producerIdFetched, setProducerIdFetched] = useState(false);

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileFetched, setProfileFetched] = useState(false);

  // Add logging for state changes
  useEffect(() => {
    console.log('🔄 Auth state changed:', {
      isAuthenticated: !!user,
      isLoading,
      userId: user?.userId,
      userEmail: user?.email
    });
  }, [user, isLoading]);

  /**
   * Proactive session timeout monitoring
   * Periodically checks if token is expired and redirects to session timeout page
   *
   * Edge cases handled:
   * - User idle with no API calls: timer catches expiration without user interaction
   * - Token deleted externally (another tab, devtools): detected by missing token
   * - Page refresh with expired token: initial check catches it immediately
   * - Multiple tabs: each tab monitors independently (no cross-tab coordination needed
   *   since redirect clears tokens which other tabs will detect)
   * - Browser sleep/hibernate: on wake, next interval check catches expired token
   * - Logout during interval: cleanup function clears interval, no stale redirects
   */
  useEffect(() => {
    // Only monitor when user is authenticated
    if (!user) return;

    const checkTokenExpiration = () => {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (!token || isTokenExpired(token)) {
        console.log('⏰ Session expired - redirecting to timeout page');
        // Clear auth data
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        // Redirect to session timeout page (using window.location for full page refresh
        // to ensure all React state is cleared)
        window.location.href = '/session-timeout';
      }
    };

    // Initial check on mount
    checkTokenExpiration();

    // Set up periodic check
    const intervalId = setInterval(checkTokenExpiration, SESSION_CHECK_INTERVAL);

    // Cleanup on unmount or when user logs out
    return () => clearInterval(intervalId);
  }, [user]);

  // Verify token on mount - decode JWT once and store in context
  useEffect(() => {
    const verifyToken = async () => {
      console.log('🔍 Token verification starting...');
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      console.log('🔍 Found token:', token ? `${token.substring(0, 20)}...` : 'null');

      if (token) {
        try {
          // Decode JWT to extract user info and roles
          const jwtPayload = decodeJwtPayload(token);

          if (jwtPayload) {
            // Roles are now directly in the JWT payload as an array
            const rolesFromToken = (jwtPayload.roles || []) as UserRole[];
            const emailFromToken = jwtPayload.email || jwtPayload.sub || '';
            const emailPrefix = emailFromToken.split('@')[0];

            const user: User = {
              userId: jwtPayload.sub || '',
              email: emailFromToken,
              firstName: emailPrefix,
              lastName: '',
              username: emailFromToken,
              roles: rolesFromToken,
            };

            setUser(user);
            console.log('✅ User restored from JWT:', user.email, 'roles:', rolesFromToken, 'raw payload:', jwtPayload);
          } else {
            // JWT decode failed - clear invalid token
            console.error('❌ Failed to decode JWT token');
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          }
        } catch (error) {
          console.error("❌ Token verification failed:", error);
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          console.log('🧹 Cleared invalid tokens from localStorage');
        }
      } else {
        console.log('ℹ️ No token found - user needs to login');
      }

      console.log('✅ Token verification completed, setting loading to false');
      setIsLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (email: string, password: string) => {
    console.log('Login attempt:', { email, password: '***' });

    try {
      // API login
      const response = await userManagementApi.auth.login({ email, password });

      // Store access token
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);

      // Store refresh token if available
      if (response.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
      }

      // Decode JWT to extract roles
      const jwtPayload = decodeJwtPayload(response.accessToken);
      // Roles are now directly in the JWT payload as an array
      const rolesFromToken = (jwtPayload?.roles || []) as UserRole[];
      console.log('🔑 JWT payload:', jwtPayload);
      console.log('🔑 Roles from token:', rolesFromToken);

      // Map user info to our User interface
      // Handle optional firstName/lastName - use email prefix as fallback
      const emailPrefix = response.user.email.split('@')[0];
      const user: User = {
        userId: response.user.userId,
        email: response.user.email,
        firstName: response.user.firstName || emailPrefix,
        lastName: response.user.lastName || '',
        username: response.user.username,
        roles: rolesFromToken.length > 0 ? rolesFromToken : response.user.roles,
      };

      setUser(user);
      console.log('🔑 Login successful for:', user.email);
    } catch (error: any) {
      // Re-throw the error - it already has status from api-client-factory
      throw error;
    }
  };

  const register = async (
    firstName: string,
    lastName: string,
    email: string,
    officialPhoneNumber: string,
    password: string,
    publisherNameEnglish: string,
    publisherNameArabic: string,
    legalEntityType: string,
    commercialRegistrationNumber: string,
    countryOfRegistration: string,
    businessLicense: File | null,
    legalRepresentativeName: string,
    nationalIdPassportNumber: string,
    profileType: 'PUBLISHER' | 'PRODUCER' = 'PUBLISHER'
  ) => {
    try {
      let businessLicenseFileId: string | undefined;

      // Step 1: Upload business license if provided
      if (businessLicense) {
        try {
          const uploadResponse = await userManagementApi.registration.uploadBusinessLicense(businessLicense);
          businessLicenseFileId = uploadResponse.fileId;
        } catch (error) {
          console.error('Failed to upload business license:', error);
          throw new Error('Failed to upload business license. Please try again.');
        }
      }

      // Step 2: Register with all data
      const registerData = {
        firstName,
        lastName,
        email,
        password,
        phoneNumber: officialPhoneNumber,
        profileType,
        publisherNameEnglish,
        publisherNameArabic,
        legalEntityType: legalEntityType as LegalEntityType,
        commercialRegistrationNumber,
        countryOfRegistration,
        businessLicenseFileId,
        legalRepresentativeName,
        representativeIdNumber: nationalIdPassportNumber,
      };

      await userManagementApi.registration.register(registerData);

      // Note: Registration returns basic user info without tokens
      // User needs to verify email and login after registration
      // Do NOT set user here - they are not authenticated until they verify and login
    } catch (error: any) {
      // Re-throw the error - it already has status from api-client-factory
      throw error;
    }
  };

  // Fetch and cache publisher ID
  const fetchPublisherId = useCallback(async (): Promise<string | null> => {
    // Return cached value if available
    if (publisherId) {
      return publisherId;
    }

    // Don't retry if we already attempted and failed
    if (publisherIdFetched) {
      return null;
    }

    // Can't fetch without user
    if (!user?.userId) {
      return null;
    }

    // Check if user has PUBLISHER role
    const isPublisher = user.roles?.includes('PUBLISHER');
    if (!isPublisher) {
      return null;
    }

    setPublisherIdLoading(true);
    setPublisherIdError(null);

    try {
      // Fetch from API
      const response = await publisherApi.publishers.getPublisherByUserId(user.userId);
      setPublisherId(response.publisherId);
      setPublisherIdFetched(true);
      console.log('✅ Publisher ID fetched and cached:', response.publisherId);
      return response.publisherId;
    } catch (error) {
      console.error('❌ Failed to fetch publisher ID:', error);
      setPublisherIdError('Failed to fetch publisher information');
      setPublisherIdFetched(true); // Mark as fetched even on error to prevent infinite retries
      return null;
    } finally {
      setPublisherIdLoading(false);
    }
  }, [user?.userId, user?.roles, publisherId, publisherIdFetched]);

  // Fetch and cache producer ID (mirrors fetchPublisherId pattern)
  const fetchProducerId = useCallback(async (): Promise<string | null> => {
    if (producerId) return producerId;
    if (producerIdFetched) return null;
    if (!user?.userId) return null;

    const isProducer = user.roles?.includes('PRODUCER');
    if (!isProducer) return null;

    setProducerIdLoading(true);
    setProducerIdError(null);

    try {
      const response = await producerService.getProducerByUserId(user.userId);
      setProducerId(response.producerId);
      setProducerIdFetched(true);
      return response.producerId;
    } catch (error) {
      console.error('❌ Failed to fetch producer ID:', error);
      setProducerIdError('Failed to fetch producer information');
      setProducerIdFetched(true);
      return null;
    } finally {
      setProducerIdLoading(false);
    }
  }, [user?.userId, user?.roles, producerId, producerIdFetched]);

  // Fetch and cache user profile
  const fetchProfile = useCallback(async (force: boolean = false): Promise<void> => {
    // Don't retry if we already fetched (unless forced)
    if ((profileFetched && !force) || !user) {
      return;
    }

    setProfileLoading(true);
    setProfileError(null);

    try {
      const fetchedProfile = await userManagementApi.profile.getProfile();
      // Store the full profile
      setProfile(fetchedProfile);
      // Update user with profile data including firstName and lastName
      setUser(prevUser => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          firstName: (fetchedProfile.profileData as any)?.firstName || prevUser.firstName,
          lastName: (fetchedProfile.profileData as any)?.lastName || prevUser.lastName,
          profileData: fetchedProfile.profileData,
        };
      });
      setProfileFetched(true);
      console.log('✅ Profile fetched and cached');
    } catch (error) {
      console.error('❌ Failed to fetch profile:', error);
      setProfileError('Failed to fetch profile');
      setProfileFetched(true); // Mark as fetched even on error to prevent infinite retries
    } finally {
      setProfileLoading(false);
    }
  }, [user, profileFetched]);

  const logout = () => {
    // For stateless JWT, clearing tokens from localStorage is sufficient
    // No server-side session to invalidate
    setUser(null);
    setPublisherId(null);
    setPublisherIdError(null);
    setPublisherIdFetched(false); // Reset so next login can try fetching again
    setProducerId(null);
    setProducerIdError(null);
    setProducerIdFetched(false);
    setProfile(null);
    setProfileError(null);
    setProfileFetched(false); // Reset so next login can fetch profile again
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    isLoading,
    // Publisher ID caching
    publisherId,
    publisherIdLoading,
    publisherIdError,
    fetchPublisherId,
    // Producer ID caching
    producerId,
    producerIdLoading,
    producerIdError,
    fetchProducerId,
    // Profile
    profile,
    profileLoading,
    profileError,
    fetchProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
